import React, { useState } from 'react';
import { FileCode, FolderLock, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Selectable } from 'kysely';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Button } from './Button';
import { Input } from './Input';
import { Skeleton } from './Skeleton';
import { useUpdatePermission } from '../helpers/usePermissionsApi';
import { FilePermissions, PermissionLevel } from '../helpers/schema';
import styles from './PermissionsPanel.module.css';

interface PermissionsFilesTabProps {
  sessionId: string;
  filePermissions: Selectable<FilePermissions>[];
  isLoading: boolean;
}

export const PermissionsFilesTab = ({
  sessionId,
  filePermissions,
  isLoading,
}: PermissionsFilesTabProps) => {
  const { mutate: updatePermission, isPending } = useUpdatePermission();
  const [newPath, setNewPath] = useState('');
  const [newLevel, setNewLevel] = useState<PermissionLevel>('view');

  const handleAddPermission = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPath.trim()) return;

    updatePermission(
      {
        sessionId,
        filePath: newPath,
        permissionLevel: newLevel,
      },
      {
        onSuccess: () => {
          toast.success('File permission added');
          setNewPath('');
          setNewLevel('view');
        },
        onError: (err) => {
          toast.error(`Failed to add: ${err.message}`);
        },
      },
    );
  };

  const handleRemovePermission = (filePath: string | null) => {
    if (!filePath) return;
    updatePermission(
      {
        sessionId,
        filePath,
        permissionLevel: 'none', // Assuming 'none' removes the override or sets it to no access
      },
      {
        onSuccess: () => toast.success('Permission removed'),
        onError: (err) => toast.error(`Failed to remove: ${err.message}`),
      },
    );
  };

  const handleUpdateLevel = (filePath: string | null, level: string) => {
    if (!filePath) return;
    updatePermission(
      {
        sessionId,
        filePath,
        permissionLevel: level as PermissionLevel,
      },
      {
        onSuccess: () => toast.success('Permission updated'),
        onError: (err) => toast.error(`Failed to update: ${err.message}`),
      },
    );
  };

  if (isLoading) {
    return (
      <div className={styles.listContainer}>
        <Skeleton style={{ height: '3rem', marginBottom: '1rem' }} />
        {[1, 2].map((i) => (
          <div key={i} className={styles.listItem}>
            <Skeleton style={{ width: '200px', height: '1.5rem' }} />
            <Skeleton style={{ width: '100px', height: '2rem' }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>
      <form onSubmit={handleAddPermission} className={styles.addForm}>
        <div className={styles.addInputGroup}>
          <Input
            placeholder="/src/* or /config.json"
            value={newPath}
            onChange={(e) => setNewPath(e.target.value)}
            className={styles.pathInput}
          />
          <Select value={newLevel} onValueChange={(val: any) => setNewLevel(val)}>
            <SelectTrigger className={styles.levelSelect}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="view">View</SelectItem>
              <SelectItem value="edit">Edit</SelectItem>
              <SelectItem value="none">None</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={isPending || !newPath.trim()}>
          <Plus size={16} /> Add Rule
        </Button>
      </form>

      <div className={styles.listContainer}>
        {filePermissions.length === 0 ? (
          <div className={styles.emptyState}>
            <FolderLock size={32} className={styles.emptyIcon} />
            <p>No specific file permissions set.</p>
          </div>
        ) : (
          filePermissions.map((perm) => (
            <div key={perm.id} className={styles.listItem}>
              <div className={styles.fileInfo}>
                <FileCode size={16} className={styles.fileIcon} />
                <span className={styles.filePath}>{perm.filePath}</span>
              </div>
              <div className={styles.actions}>
                <Select
                  value={perm.permissionLevel}
                  onValueChange={(val) => handleUpdateLevel(perm.filePath, val)}
                >
                  <SelectTrigger className={styles.permissionSelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">View</SelectItem>
                    <SelectItem value="edit">Edit</SelectItem>
                    <SelectItem value="none">None</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => handleRemovePermission(perm.filePath)}
                  className={styles.deleteButton}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
